import * as THREE from 'three';
import Wall from '../models/wall.js';
import Door from '../models/door.js';
import Window from '../models/window.js';

class WallController {
  constructor(sceneManager) {
    this.scene = sceneManager.getScene();
    this.walls = [];
    this.minimumWallLength = 0.2;
    
    // Stato disegno
    this.isDrawing = false;
    this.drawingMode = false;
    this.startPoint = null;
    this.currentWall = null;
    
    // Preview durante disegno
    this.previewLine = null;
    this.previewMarker = null;
    
    // Riferimento ai controls per disabilitarli
    this.controls = null;
    
    // Riferimenti ai controller per cleanup porte/finestre
    this.doorController = null;
    this.windowController = null;
    
    this.setupPreview();
    this.setupMeasurementLabel();
  }
  
  setControls(controls) {
    this.controls = controls;
  }
  
  setDoorController(doorController) {
    this.doorController = doorController;
  }
  
  setWindowController(windowController) {
    this.windowController = windowController;
  }
  
  setupPreview() {
    // Linea preview tratteggiata
    const lineMaterial = new THREE.LineDashedMaterial({
      color: 0x00cc00,
      dashSize: 0.3,
      gapSize: 0.2,
      linewidth: 2
    });
    
    const lineGeometry = new THREE.BufferGeometry();
    this.previewLine = new THREE.Line(lineGeometry, lineMaterial);
    this.previewLine.visible = false;
    this.previewLine.frustumCulled = false;
    this.scene.add(this.previewLine);
    
    // Marker punto di partenza
    const markerGeometry = new THREE.SphereGeometry(0.15, 16, 16);
    const markerMaterial = new THREE.MeshBasicMaterial({ color: 0x00cc00 });
    this.previewMarker = new THREE.Mesh(markerGeometry, markerMaterial);
    this.previewMarker.visible = false;
    this.scene.add(this.previewMarker);
  }

  setupMeasurementLabel() {
    this.measurementLabel = document.createElement('div');
    this.measurementLabel.id = 'wall-measurement-label';
    Object.assign(this.measurementLabel.style, {
      position:      'fixed',
      background:    'rgba(10, 10, 30, 0.85)',
      color:         '#ffffff',
      padding:       '5px 13px',
      borderRadius:  '20px',
      fontSize:      '15px',
      fontWeight:    'bold',
      fontFamily:    'system-ui, sans-serif',
      letterSpacing: '0.03em',
      pointerEvents: 'none',
      display:       'none',
      zIndex:        '2000',
      whiteSpace:    'nowrap',
      border:        '1.5px solid rgba(0,204,0,0.75)',
      boxShadow:     '0 2px 10px rgba(0,0,0,0.45)',
      transform:     'translate(-50%, -200%)',
    });
    document.body.appendChild(this.measurementLabel);
  }

  updateMeasurementLabel(endPoint, screenX, screenY) {
    if (!this.measurementLabel || !this.isDrawing || !this.startPoint) return;

    const snappedEnd = this.snapToGrid(endPoint);
    const length = this.startPoint.distanceTo(snappedEnd);

    const lengthText = length < 1
      ? `${(length * 100).toFixed(0)} cm`
      : `${length.toFixed(2)} m`;

    const dx = snappedEnd.x - this.startPoint.x;
    const dz = snappedEnd.z - this.startPoint.z;
    let absAngle = THREE.MathUtils.radToDeg(Math.atan2(-dz, dx));
    absAngle = ((absAngle % 180) + 180) % 180;

    this.measurementLabel.textContent = `${lengthText}  ·  ${absAngle.toFixed(0)}°`;
    this.measurementLabel.style.left    = `${screenX}px`;
    this.measurementLabel.style.top     = `${screenY}px`;
    this.measurementLabel.style.display = 'block';
    this.measurementLabel.style.borderColor = length >= this.minimumWallLength
      ? 'rgba(0,204,0,0.75)'
      : 'rgba(255,60,60,0.85)';
  }

  hideMeasurementLabel() {
    if (this.measurementLabel) {
      this.measurementLabel.style.display = 'none';
    }
  }

  enableDrawMode() {
    this.drawingMode = true;
    if (this.controls) {
      this.controls.enableRotate = false;
      this.controls.enablePan = true;
      this.controls.enableZoom = true;
    }
    console.log('Modalità disegno pareti: ATTIVA');
  }
  
  disableDrawMode() {
    this.drawingMode = false;
    this.cancelDrawing();
    if (this.controls) {
      this.controls.enableRotate = true;
      this.controls.enablePan = true;
      this.controls.enableZoom = true;
    }
    console.log('Modalità disegno pareti: DISATTIVATA');
  }
  
  toggleDrawMode() {
    if (this.drawingMode) {
      this.disableDrawMode();
    } else {
      this.enableDrawMode();
    }
  }
  
  startDrawing(point) {
    if (!this.drawingMode) return;
    
    this.isDrawing = true;
    this.startPoint = this.snapToGrid(point);
    
    this.previewMarker.position.copy(this.startPoint);
    this.previewMarker.position.y = 0.1;
    this.previewMarker.visible = true;
    
    console.log('Disegno parete iniziato:', this.startPoint);
  }
  
  updateDrawing(point) {
    if (!this.isDrawing) return;
    
    const endPoint = this.snapToGrid(point);
    
    const snapDistance = 0.3;
    let isSnapped = false;
    
    for (const wall of this.walls) {
      if (point.distanceTo(wall.start) < snapDistance || 
          point.distanceTo(wall.end) < snapDistance) {
        isSnapped = true;
        break;
      }
    }
    
    if (isSnapped) {
      this.previewLine.material.color.setHex(0xffcc00);
    } else {
      this.previewLine.material.color.setHex(0x00cc00);
    }
    
    const positions = new Float32Array([
      this.startPoint.x, 0.1, this.startPoint.z,
      endPoint.x, 0.1, endPoint.z
    ]);
    
    this.previewLine.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3)
    );
    this.previewLine.computeLineDistances();
    this.previewLine.visible = true;
  }
  
  finishDrawing(point) {
    if (!this.isDrawing) return;
    
    let startPoint = this.startPoint.clone();
    let endPoint = this.snapToGrid(point);
    
    const distance = startPoint.distanceTo(endPoint);
    if (distance < this.minimumWallLength) {
      console.log('Parete troppo corta, annullata');
      this.cancelDrawing();
      return;
    }
    
    // Salva le posizioni canoniche (pre-estensione) prima di aggiungere i raccordi d'angolo
    const canonicalStart = startPoint.clone();
    const canonicalEnd   = endPoint.clone();

    const extendedPoints = this.extendWallForCorners(startPoint, endPoint);
    startPoint = extendedPoints.start;
    endPoint = extendedPoints.end;
    
    const wall = new Wall(startPoint, endPoint);
    // Sovrascrive le posizioni canoniche: la parete "disegnata" va da canonicalStart a canonicalEnd
    wall.canonicalStart.copy(canonicalStart);
    wall.canonicalEnd.copy(canonicalEnd);
    this.addWall(wall);
    console.log('Parete creata:', wall);
    
    this.isDrawing = false;
    this.startPoint = null;
    this.previewLine.visible = false;
    this.previewMarker.visible = false;
    this.hideMeasurementLabel();
  }
  
  extendWallForCorners(start, end) {
    const snapDistance = 0.3;
    const wallThickness = 0.2;
    const extensionAmount = wallThickness / 2;
    
    let extendedStart = start.clone();
    let extendedEnd = end.clone();
    
    const direction = new THREE.Vector3().subVectors(end, start).normalize();
    
    for (const wall of this.walls) {
      if (start.distanceTo(wall.start) < snapDistance || start.distanceTo(wall.end) < snapDistance) {
        extendedStart.sub(direction.clone().multiplyScalar(extensionAmount));
      }
      
      if (end.distanceTo(wall.start) < snapDistance || end.distanceTo(wall.end) < snapDistance) {
        extendedEnd.add(direction.clone().multiplyScalar(extensionAmount));
      }
    }
    
    return {
      start: extendedStart,
      end: extendedEnd
    };
  }
  
  cancelDrawing() {
    this.isDrawing = false;
    this.startPoint = null;
    this.previewLine.visible = false;
    this.previewMarker.visible = false;
    this.hideMeasurementLabel();
  }
  
  addWall(wall) {
    const mesh = wall.createMesh();
    this.scene.add(mesh);
    this.walls.push(wall);
    return wall;
  }
  
  removeWall(wallId) {
    const index = this.walls.findIndex(w => w.id === wallId);
    if (index === -1) return false;
    
    const wall = this.walls[index];
    
    // Rimuove tutte le porte sulla parete
    if (this.doorController && wall.doors && wall.doors.length > 0) {
      const doorsToRemove = [...wall.doors];
      doorsToRemove.forEach(door => {
        this.doorController.removeDoor(door.id);
      });
    }
    
    // Rimuove tutte le finestre sulla parete
    if (this.windowController && wall.windows && wall.windows.length > 0) {
      const windowsToRemove = [...wall.windows];
      windowsToRemove.forEach(win => {
        this.windowController.removeWindow(win.id);
      });
    }
    
    this.scene.remove(wall.mesh);
    wall.dispose();
    this.walls.splice(index, 1);
    
    return true;
  }
  
  /**
   * Sposta una parete aggiornando i punti di inizio e fine.
   * Aggiorna automaticamente le porte e finestre ad essa collegate.
   */
  moveWall(wallId, newStart, newEnd) {
    const wall = this.getWallById(wallId);
    if (!wall) return false;
    
    wall.start.copy(newStart);
    wall.end.copy(newEnd);
    wall.start.y = 0;
    wall.end.y = 0;
    
    // Ricostruisce i segmenti della parete
    wall.rebuildWallSegments();
    
    // Aggiorna le posizioni di porte e finestre
    if (wall.doors) {
      wall.doors.forEach(door => door.updatePosition());
    }
    if (wall.windows) {
      wall.windows.forEach(win => win.updatePosition());
    }
    
    return true;
  }
  
  /**
   * Ruota una parete attorno al suo centro di degrees gradi (asse Y).
   * Porte e finestre vengono aggiornate di conseguenza.
   */
  rotateWall(wallId, degrees) {
    const wall = this.getWallById(wallId);
    if (!wall) return false;
    
    const center = wall.getCenter();
    const angle = THREE.MathUtils.degToRad(degrees);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    
    // Ruota il punto iniziale attorno al centro
    const startDelta = new THREE.Vector3().subVectors(wall.start, center);
    wall.start.set(
      center.x + startDelta.x * cos - startDelta.z * sin,
      0,
      center.z + startDelta.x * sin + startDelta.z * cos
    );
    
    // Ruota il punto finale attorno al centro
    const endDelta = new THREE.Vector3().subVectors(wall.end, center);
    wall.end.set(
      center.x + endDelta.x * cos - endDelta.z * sin,
      0,
      center.z + endDelta.x * sin + endDelta.z * cos
    );
    
    // Ricostruisce i segmenti
    wall.rebuildWallSegments();
    
    // Dopo la rotazione la nuova geometria diventa il riferimento canonico
    wall.canonicalStart.copy(wall.start);
    wall.canonicalEnd.copy(wall.end);
    
    // Aggiorna porte e finestre (positionOnWall rimane invariato, ma angolo cambia)
    if (wall.doors) {
      wall.doors.forEach(door => door.updatePosition());
    }
    if (wall.windows) {
      wall.windows.forEach(win => win.updatePosition());
    }
    
    console.log(`Parete ruotata di ${degrees}°`);
    return true;
  }
  
  /**
   * Duplica una parete con un offset
   * Copia anche le porte e finestre presenti sulla parete originale
   */
  duplicateWall(wallId) {
    const wall = this.getWallById(wallId);
    if (!wall) return null;
    
    const offset = new THREE.Vector3(0, 0, 1);
    const newStart = wall.start.clone().add(offset);
    const newEnd = wall.end.clone().add(offset);
    
    const newWall = new Wall(newStart, newEnd, wall.height, wall.thickness);
    this.addWall(newWall);
    
    // Copia le porte sulla nuova parete
    if (this.doorController && wall.doors && wall.doors.length > 0) {
      wall.doors.forEach(door => {
        const newDoor = new Door(newWall, door.positionOnWall, door.width, door.height, door.doorType ?? 'swing');
        newDoor.isFlipped = door.isFlipped;
        this.doorController.addDoor(newDoor);
      });
    }
    
    // Copia le finestre sulla nuova parete
    if (this.windowController && wall.windows && wall.windows.length > 0) {
      wall.windows.forEach(win => {
        const newWin = new Window(newWall, win.positionOnWall, win.width, win.height, win.sillHeight);
        newWin.isFlipped = win.isFlipped;
        this.windowController.addWindow(newWin);
      });
    }
    
    console.log(`Parete duplicata (${wall.doors?.length || 0} porte, ${wall.windows?.length || 0} finestre)`);
    return newWall;
  }
  
  getWallById(id) {
    return this.walls.find(w => w.id === id);
  }
  
  snapToGrid(point, gridSize = 0.1) {
    const snapDistance = 0.3;
    
    for (const wall of this.walls) {
      const distToStart = point.distanceTo(wall.start);
      if (distToStart < snapDistance) {
        return wall.start.clone();
      }
      
      const distToEnd = point.distanceTo(wall.end);
      if (distToEnd < snapDistance) {
        return wall.end.clone();
      }
    }
    
    return new THREE.Vector3(
      Math.round(point.x / gridSize) * gridSize,
      0,
      Math.round(point.z / gridSize) * gridSize
    );
  }
  
  getAllWalls() {
    return this.walls;
  }
   
  /**
   * Rimuove solo le pareti (porte e finestre comprese) senza distruggere
   * le risorse di preview. Usare al posto di dispose() quando il controller
   * viene riutilizzato (es. caricamento progetto)
   */
  clearWalls() {
    this.walls.forEach(wall => {
      if (this.doorController && wall.doors?.length) {
        [...wall.doors].forEach(door => this.doorController.removeDoor(door.id));
      }
      if (this.windowController && wall.windows?.length) {
        [...wall.windows].forEach(win => this.windowController.removeWindow(win.id));
      }
      this.scene.remove(wall.mesh);
      wall.dispose();
    });
    this.walls = [];

    this.cancelDrawing(); // resetta lo stato di disegno in modo pulito
  }
  
  dispose() {
    this.walls.forEach(wall => {
      this.scene.remove(wall.mesh);
      wall.dispose();
    });
    this.walls = [];
    
    this.scene.remove(this.previewLine);
    this.scene.remove(this.previewMarker);
    this.previewLine.geometry.dispose();
    this.previewLine.material.dispose();
    this.previewMarker.geometry.dispose();
    this.previewMarker.material.dispose();

    if (this.measurementLabel && this.measurementLabel.parentNode) {
      this.measurementLabel.parentNode.removeChild(this.measurementLabel);
      this.measurementLabel = null;
    }
  }
}

export default WallController;